import { CustomAttributeFieldType } from '@risksmart-app/form-configuration/src/field-types/types';
import type { FormFieldOption } from '@risksmart-app/form-configuration/src/types';

import type { CustomAttributeOptionField } from './buildActiveFormDescriptor';
import type { DescriptorFieldOption } from './types';

/**
 * Minimal shape this module needs from a custom-attribute render prop — the
 * JSONForms-derived descriptor produced by `getCustomAttributeRenderProps`.
 * Declared structurally rather than importing `FieldRendererProps` so this stays
 * a pure, provider-free module the unit tests can drive directly.
 */
export interface CustomAttributeRenderProp {
  type: string;
  label: string;
  path: string;
  options?: FormFieldOption[];
}

/**
 * The two closed-option custom-attribute field types, mapped to whether the
 * stored value is an array. Everything else a custom attribute can be (text,
 * textarea, number, date, link, attach-file, users, tags, departments) is not a
 * closed set and is not prefilled here.
 */
const OPTION_FIELD_TYPES: Partial<Record<string, { multiple: boolean }>> = {
  [CustomAttributeFieldType.Select]: { multiple: false },
  [CustomAttributeFieldType.MultiSelect]: { multiple: true },
};

/**
 * The value a custom-attribute option WRITES, versus the label it SHOWS.
 *
 * An option is either a plain `StringOption` (label and value are the same
 * string) or an `AltValueOption`, where `Value` is the display label and
 * `AltValue` is what is actually stored — the "alternate value" affordance that
 * lets a customer relabel an option without rewriting historical rows. Getting
 * this the wrong way round would write a label into the record, so it is
 * resolved once here rather than at each call site.
 *
 * Both are strings: a custom-attribute select is a JSON-schema string enum, so
 * unlike a rating taxonomy there is no numeric variant to preserve.
 */
const toDescriptorOption = (
  option: FormFieldOption
): DescriptorFieldOption => ({
  value: option._tag === 'AltValueOption' ? option.AltValue : option.Value,
  label: option.Value,
});

/**
 * Build the closed-option custom-attribute lookup `buildActiveFormDescriptor`
 * consumes, keyed by the attribute's bare schema path (the descriptor's
 * react-hook-form key is `CustomAttributeData.<path>`).
 *
 * Attributes with no options are omitted rather than published empty — a select
 * with no configured options is not a field prefill can write, and the
 * descriptor's fail-closed rule treats "no options" as "not prefillable".
 */
export const resolveCustomAttributeOptionFields = (
  renderProps: CustomAttributeRenderProp[]
): Map<string, CustomAttributeOptionField> => {
  const fields = new Map<string, CustomAttributeOptionField>();

  for (const renderProp of renderProps) {
    const optionField = OPTION_FIELD_TYPES[renderProp.type];
    if (!optionField) {
      continue;
    }

    const options = (renderProp.options ?? []).map(toDescriptorOption);
    if (options.length === 0) {
      continue;
    }

    fields.set(renderProp.path, {
      label: renderProp.label,
      multiple: optionField.multiple,
      options,
    });
  }

  return fields;
};
